export interface PlayerStats {
  playerId: string;
  playerName: string;
  damageDealt: number;
  kills: number;
  deaths: number;
  shotsFired: number;
  shotsHit: number;
}

export class StatsPanel {
  private stats: Map<string, PlayerStats> = new Map();
  private container: HTMLElement;
  private visible: boolean = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.container.style.display = 'none';
    this.addStyles();
  }

  private getOrCreateStats(playerId: string, playerName?: string): PlayerStats {
    if (!this.stats.has(playerId)) {
      this.stats.set(playerId, {
        playerId,
        playerName: playerName || 'Unknown',
        damageDealt: 0,
        kills: 0,
        deaths: 0,
        shotsFired: 0,
        shotsHit: 0,
      });
    }
    return this.stats.get(playerId)!;
  }

  recordDamage(playerId: string, playerName: string, damage: number): void {
    const stats = this.getOrCreateStats(playerId, playerName);
    stats.damageDealt += Math.round(damage);
  }

  recordKill(killerId: string, killerName: string): void {
    const stats = this.getOrCreateStats(killerId, killerName);
    stats.kills++;
  }

  recordDeath(victimId: string, victimName: string): void {
    const stats = this.getOrCreateStats(victimId, victimName);
    stats.deaths++;
  }

  recordShot(playerId: string, playerName: string, hit: boolean): void {
    const stats = this.getOrCreateStats(playerId, playerName);
    stats.shotsFired++;
    if (hit) stats.shotsHit++;
  }

  toggle(): void {
    this.visible = !this.visible;
    this.render();
  }

  show(): void {
    this.visible = true;
    this.render();
  }

  hide(): void {
    this.visible = false;
    this.render();
  }

  render(): void {
    if (!this.visible) {
      this.container.style.display = 'none';
      return;
    }

    this.container.style.display = 'flex';

    const sortedStats = Array.from(this.stats.values()).sort(
      (a, b) => b.damageDealt - a.damageDealt,
    );

    if (sortedStats.length === 0) {
      this.container.innerHTML = `
        <div class="stats-panel">
          <h2>Statistics</h2>
          <p class="no-stats">No statistics yet</p>
          <div class="stats-hint">Press Shift+TAB to close</div>
        </div>
      `;
      return;
    }

    this.container.innerHTML = `
      <div class="stats-panel">
        <h2>Statistics</h2>
        <table class="stats-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Damage</th>
              <th>Kills</th>
              <th>Deaths</th>
              <th>Accuracy</th>
            </tr>
          </thead>
          <tbody>
            ${sortedStats
              .map(
                (s, i) => `
              <tr class="${i === 0 ? 'mvp' : ''}">
                <td class="player-name">${this.escapeHtml(s.playerName)}</td>
                <td class="damage">${s.damageDealt}</td>
                <td class="kills">${s.kills}</td>
                <td class="deaths">${s.deaths}</td>
                <td class="accuracy">${s.shotsFired > 0 ? Math.round((s.shotsHit / s.shotsFired) * 100) : 0}%</td>
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>
        <div class="stats-hint">Press Shift+TAB to close</div>
      </div>
    `;
  }

  getStats(): PlayerStats[] {
    return Array.from(this.stats.values());
  }

  setStats(stats: PlayerStats[]): void {
    this.stats.clear();
    for (const s of stats) {
      this.stats.set(s.playerId, { ...s });
    }
    if (this.visible) {
      this.render();
    }
  }

  reset(): void {
    this.stats.clear();
    if (this.visible) {
      this.render();
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private addStyles(): void {
    if (document.getElementById('stats-panel-styles')) return;

    const style = document.createElement('style');
    style.id = 'stats-panel-styles';
    style.textContent = `
      #stats-panel-container {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .stats-panel {
        background: #1a1a1a;
        border: 2px solid #444;
        border-radius: 12px;
        padding: 30px;
        max-width: 600px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
      }
      
      .stats-panel h2 {
        margin: 0 0 20px 0;
        text-align: center;
        color: #fff;
        font-size: 24px;
      }
      
      .stats-table {
        width: 100%;
        border-collapse: collapse;
        color: #fff;
      }
      
      .stats-table th {
        background: #333;
        padding: 10px;
        text-align: left;
        font-weight: bold;
        border-bottom: 2px solid #555;
      }
      
      .stats-table td {
        padding: 10px;
        border-bottom: 1px solid #333;
      }
      
      .stats-table tr.mvp {
        background: rgba(255, 215, 0, 0.1);
      }
      
      .stats-table tr.mvp .player-name {
        color: #ffd700;
        font-weight: bold;
      }
      
      .stats-table tr.mvp .player-name::before {
        content: '👑 ';
      }
      
      .stats-table .damage {
        color: #ff4444;
        font-weight: bold;
      }
      
      .stats-table .kills {
        color: #44ff44;
      }
      
      .stats-table .deaths {
        color: #ff8844;
      }
      
      .stats-table .accuracy {
        color: #44aaff;
      }
      
      .stats-hint {
        text-align: center;
        color: #888;
        font-size: 14px;
        margin-top: 20px;
      }
      
      .no-stats {
        text-align: center;
        color: #888;
        padding: 40px 20px;
      }
    `;
    document.head.appendChild(style);
  }

  destroy(): void {
    this.container.innerHTML = '';
    this.stats.clear();
  }
}
